import React, {useMemo} from 'react';
import {useSelector} from 'react-redux';
import PageButton from '../../elements/PageButton/PageButton';
import {usePageAnalytics} from '../../../hooks/analytics/usePageAnalytics';
import {getCampaignMissions} from '../../../configs/campaigns';
import {getTutorialChapterReward} from '../../../utils/tutorial/getTutorialChapterReward';
import {getFactionConfig} from '../../../utils/factions/getFactionConfig';
import {FACTION_NAME_GENITIVE} from '../../../configs/factionCampaigns';
import {MissionRewardBadge} from '../TutorialMissionsPage/MissionRewardBadge';
import '../TutorialMissionsPage/TutorialMissionsPage.css';

const PAGE_ID = 'CampaignMissionsPage';

/**
 * Список миссий сюжетной кампании фракции. Устроен как список глав обучения:
 * последовательная разблокировка (миссия N доступна после N-1), плашка
 * награды у непройденных, галочка у пройденных.
 */
const CampaignMissionsPage = ({factionId, onStartMission, onBack}) => {
  usePageAnalytics(PAGE_ID);

  const completedMissions = useSelector((state) => state.private.auth.completedCampaignMissions);
  const isAdmin = useSelector((state) => state.private.auth.isAdmin);
  const isSuperAdmin = useSelector((state) => state.private.auth.isSuperAdmin);
  const availableFactions = useSelector((state) => state.private.auth.availableFactions);
  const isAllFactionsAvailable = useSelector((state) => state.private.auth.isAllFactionsAvailable);

  const ownedFactionIds = useMemo(
    () => (availableFactions || []).filter((faction) => faction.isAvailable).map((faction) => faction.id),
    [availableFactions]
  );

  // Невыпущенные миссии (isAdminOnly) в списке видит только админ
  const missions = getCampaignMissions(factionId, {isAdmin: isAdmin || isSuperAdmin});
  const factionConfig = getFactionConfig(factionId);
  const completed = completedMissions || [];

  return (
    <>
      <PageButton
        className={'tutorial-missions-floating-back-button'}
        onClick={onBack}
        pageId={PAGE_ID}
        buttonId={'back_floating'}
      >
        <img
          className={'tutorial-missions-floating-back-button_icon'}
          src={'/img/icons/back_navigation.png'}
          alt={'Назад'}
          title={'Назад'}
        />
      </PageButton>

      <h1 className={'tutorial-missions-page_title'} style={{color: factionConfig?.textColor}}>
        {`Кампания ${FACTION_NAME_GENITIVE[factionId] || factionConfig?.name || ''}`}
      </h1>

      <div className={'tutorial-missions-page_list'}>
        {missions.map((mission, index) => {
          const isCompleted = completed.includes(mission.id);
          const previousMission = missions[index - 1];
          const isLocked = Boolean(previousMission) && !completed.includes(previousMission.id);
          const reward = getTutorialChapterReward(mission, {ownedFactionIds, isAllFactionsAvailable});

          return (
            <PageButton
              key={mission.id}
              pageId={PAGE_ID}
              buttonId={`mission_${mission.id}`}
              className={'tutorial-missions-page_mission'}
              onClick={() => onStartMission(mission.id)}
              disabled={isLocked}
              title={isLocked ? 'Сначала пройди предыдущую миссию' : mission.description}
            >
              <span className={'tutorial-missions-page_mission-main'}>
                <span className={'tutorial-missions-page_mission-status'}>
                  {isCompleted && <img src={'/img/icons/check.png'} alt={'Пройдено'}/>}
                  {!isCompleted && isLocked && <img src={'/img/icons/lock_grey.png'} alt={'Недоступно'}/>}
                  {!isCompleted && !isLocked && <img src={'/img/icons/rules_white.png'} alt={'Доступно'}/>}
                </span>
                <span className={'tutorial-missions-page_mission-info'}>
                  <span className={'tutorial-missions-page_mission-title'}>{mission.title}</span>
                  <span className={'tutorial-missions-page_mission-description'}>{mission.description}</span>
                </span>
              </span>
              {!isCompleted && <MissionRewardBadge reward={reward}/>}
            </PageButton>
          );
        })}
      </div>

      <div className={'main-menu-buttons'}>
        <PageButton onClick={onBack} pageId={PAGE_ID} buttonId={'back'}>
          Назад
        </PageButton>
      </div>
    </>
  );
};

export default CampaignMissionsPage;
